/**
 * Realtime Client Factory
 *
 * Factory function to create a StreamingPostClient for real-time event streaming.
 */

import StreamingPostClient from "~vue/StreamingPostClient.ts";
import { logError } from "~vue/utils/logUtils";

/**
 * Validate client options object
 *
 * @param {Object} options - Client options
 * @returns {Object} Validated and normalized options
 * @private
 */
function validateOptions(options) {
    if (options == null) {
        return {};
    }

    if (typeof options !== "object") {
        console.warn("createRealtimeClient: options must be an object, using defaults");
        return {};
    }

    const validated = {};

    // Validate reconnectEnabled
    if (options.reconnectEnabled !== undefined) {
        validated.reconnectEnabled = Boolean(options.reconnectEnabled);
    }

    // Validate reconnectInterval
    if (options.reconnectInterval !== undefined) {
        const interval = Number(options.reconnectInterval);
        if (Number.isNaN(interval) || interval < 0) {
            console.warn(`createRealtimeClient: Invalid reconnectInterval ${options.reconnectInterval}, using default`);
        } else {
            validated.reconnectInterval = interval;
        }
    }

    return validated;
}

/**
 * Create a StreamingPostClient for real-time event streaming.
 *
 * @param {string} streamUrl - Streaming POST URL (http:// or https://)
 * @param {Object} [options={}] - Client options (reconnect settings, etc.)
 * @returns {StreamingPostClient} Real-time client instance
 * @throws {Error} If URL is invalid or options are malformed
 *
 * @example
 * const client = createRealtimeClient('https://example.com/events/stream/c/user123/window456/', { reconnectEnabled: true });
 */
export function createRealtimeClient(streamUrl, options = {}) {
    if (!streamUrl || typeof streamUrl !== "string") {
        throw new Error("createRealtimeClient: streamUrl parameter is required and must be a string");
    }

    const validatedOptions = validateOptions(options);

    try {
        return new StreamingPostClient(streamUrl, validatedOptions);
    } catch (error) {
        logError(error, {
            context: {
                component: "RealtimeClientFactory",
                clientType: "StreamingPost",
                url: streamUrl,
                options: validatedOptions,
            },
            tags: {
                error_type: "realtime_client_creation",
            },
        });
        throw error;
    }
}
